import React, { useState, useRef, useEffect } from "react";

/**
 * Small click-to-edit primitive.
 * - `type="number"` accepts numeric input; formatter renders read-only view.
 * - `type="text"` for string fields.
 * Enter commits · Escape cancels · Blur commits.
 */
export const InlineEdit = ({
    value,
    onSave,
    type = "text",
    format = (v) => String(v),
    min,
    max,
    step,
    className = "",
    inputClassName = "",
    testId,
    placeholder = "",
    disabled = false,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        if (editing && ref.current) {
            ref.current.focus();
            ref.current.select();
        }
    }, [editing]);

    const commit = () => {
        setEditing(false);
        if (type === "number") {
            const clean = draft.replace(/[^0-9.\-]/g, "");
            const n = Number(clean);
            if (!Number.isNaN(n) && clean !== "") {
                let final = n;
                if (typeof min === "number") final = Math.max(min, final);
                if (typeof max === "number") final = Math.min(max, final);
                if (final !== value) onSave(final);
            }
        } else {
            const v = draft.trim();
            if (v && v !== value) onSave(v);
        }
    };

    const cancel = () => setEditing(false);

    if (disabled) {
        return <span className={className}>{format(value)}</span>;
    }

    return editing ? (
        <input
            ref={ref}
            type={type}
            step={step}
            min={min}
            max={max}
            defaultValue={value}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commit(); }
                else if (e.key === "Escape") { e.preventDefault(); cancel(); }
            }}
            data-testid={testId ? `${testId}-edit` : undefined}
            className={`px-2 py-0.5 rounded-md bg-[hsl(var(--secondary))] outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/50 border border-[hsl(var(--primary))]/40 ${inputClassName}`}
        />
    ) : (
        <button
            type="button"
            onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
            title="Click to edit"
            data-testid={testId ? `${testId}-view` : undefined}
            className={`px-2 py-0.5 rounded-md hover:bg-[hsl(var(--secondary))]/60 transition-colors cursor-text text-left ${className}`}
        >
            {format(value)}
        </button>
    );
};

export default InlineEdit;
